import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';
import Navigation from '../components/layout/Navigation';
import Footer from '../components/layout/Footer';
import Stepper from '../components/stepper/Stepper';
import { toast } from 'react-hot-toast';

const defaultExperience = {
  job_title: '',
  start_date: '',
  end_date: '',
  responsibilities: ''
};

function WorkExperience() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isFresher, setIsFresher] = useState(null);
  const [experienceList, setExperienceList] = useState([{ ...defaultExperience }]);

  useEffect(() => {
    if (user) {
      fetchWorkExperience();
    }
  }, [user]);

  const fetchWorkExperience = async () => {
    try {
      const { data, error } = await supabase
        .from('profile_work_experience')
        .select('*')
        .eq('user_id', user.id);

      if (error) throw error;

      if (data && data.length > 0) {
        setIsFresher(data[0].is_fresher);
        if (!data[0].is_fresher) {
          setExperienceList(data.map(exp => ({
            job_title: exp.job_title,
            start_date: exp.start_date,
            end_date: exp.end_date,
            responsibilities: exp.responsibilities
          })));
        }
      }
    } catch (error) {
      console.error('Error fetching work experience:', error);
      toast.error('Failed to load work experience data');
    }
  };

  const handleInputChange = (index, field, value) => {
    const newExperienceList = [...experienceList];
    newExperienceList[index] = {
      ...newExperienceList[index],
      [field]: value
    };
    setExperienceList(newExperienceList);
  };

  const addMoreExperience = () => {
    setExperienceList([...experienceList, { ...defaultExperience }]);
  };

  const removeExperience = (index) => {
    if (experienceList.length > 1) {
      const newExperienceList = experienceList.filter((_, i) => i !== index);
      setExperienceList(newExperienceList);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Delete existing records
      const { error: deleteError } = await supabase
        .from('profile_work_experience')
        .delete()
        .eq('user_id', user.id);

      if (deleteError) throw deleteError;

      // Insert new record
      const { error: insertError } = await supabase
        .from('profile_work_experience')
        .insert(
          isFresher 
            ? [{ user_id: user.id, is_fresher: true }]
            : experienceList.map(exp => ({
                user_id: user.id,
                is_fresher: false,
                job_title: exp.job_title,
                start_date: exp.start_date,
                end_date: exp.end_date,
                responsibilities: exp.responsibilities
              }))
        );

      if (insertError) throw insertError;

      toast.success('Work experience saved successfully!');
      navigate('/skills-certifications');
    } catch (error) {
      console.error('Error saving work experience:', error);
      toast.error('Failed to save work experience. Please try again.');
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  if (isFresher === null) {
    return (
      <div className="min-h-screen flex flex-col bg-primary-50">
        <Navigation />
        <div className="flex-grow pt-16 md:pt-20 mt-2 md:mt-5">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            {/* Mobile Stepper */}
            <div className="block md:hidden mb-6">
              <Stepper />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
              {/* Desktop Stepper */}
              <div className="hidden md:block md:col-span-3">
                <div className="sticky top-24">
                  <Stepper />
                </div>
              </div>

              <div className="col-span-1 md:col-span-9">
                <div className="bg-primary-extra rounded-lg shadow-sm p-4 md:p-8">
                  <h1 className="text-xl md:text-2xl font-bold text-primary-500 mb-6 md:mb-8">Work Experience</h1>
                  <div className="space-y-6">
                    <p className="text-base md:text-lg text-primary-text">Are you a fresher?</p>
                    <div className="space-y-4">
                      <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4">
                        <button
                          onClick={() => setIsFresher(true)}
                          className="w-full sm:w-auto px-4 md:px-6 py-2.5 md:py-3 bg-primary-500 text-primary-50 rounded-md hover:bg-opacity-90"
                        >
                          Yes, I'm a Fresher
                        </button>
                        <button
                          onClick={() => setIsFresher(false)}
                          className="w-full sm:w-auto px-4 md:px-6 py-2.5 md:py-3 border border-primary-200 text-primary-text rounded-md hover:bg-primary-100"
                        >
                          No, I have Experience
                        </button>
                      </div>
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => navigate('/education-background')}
                          className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-2.5 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100 text-sm"
                        >
                          Back to Education
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isFresher) {
    return (
      <div className="min-h-screen flex flex-col bg-primary-50">
        <Navigation />
        <div className="flex-grow pt-16 md:pt-20 mt-2 md:mt-5">
          <div className="max-w-[1440px] mx-auto px-4 md:px-6">
            {/* Mobile Stepper */}
            <div className="block md:hidden mb-6">
              <Stepper />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
              {/* Desktop Stepper */}
              <div className="hidden md:block md:col-span-3">
                <div className="sticky top-24">
                  <Stepper />
                </div>
              </div>

              <div className="col-span-1 md:col-span-9">
                <div className="bg-primary-extra rounded-lg shadow-sm p-4 md:p-8">
                  <h1 className="text-xl md:text-2xl font-bold text-primary-500 mb-6 md:mb-8">Work Experience</h1>
                  <div className="space-y-6">
                    <p className="text-base md:text-lg text-primary-text">You have selected that you are a fresher.</p>
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="flex flex-col sm:flex-row justify-between gap-3 sm:space-x-4">
                        <button
                          type="button"
                          onClick={() => setIsFresher(null)}
                          className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-2.5 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100 text-sm"
                        >
                          Back
                        </button>
                        <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4">
                          <button
                            type="button"
                            onClick={() => navigate('/education-background')}
                            className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-2.5 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100 text-sm"
                          >
                            Back to Education
                          </button>
                          <button
                            type="submit"
                            className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-2.5 bg-primary-500 text-primary-50 rounded-md hover:bg-opacity-90 text-sm"
                          >
                            Save & Next
                          </button>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-primary-50">
      <Navigation />
      <div className="flex-grow pt-16 md:pt-20 mt-2 md:mt-5 mb-4">
        <div className="max-w-[1440px] mx-auto px-4 md:px-6">
          {/* Mobile Stepper */}
          <div className="block md:hidden mb-6">
            <Stepper />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-8">
            {/* Desktop Stepper */}
            <div className="hidden md:block md:col-span-3">
              <div className="sticky top-24">
                <Stepper />
              </div>
            </div>

            <div className="col-span-1 md:col-span-9">
              <div className="bg-primary-extra rounded-lg shadow-sm p-4 md:p-8">
                <h1 className="text-xl md:text-2xl font-bold text-primary-500 mb-6 md:mb-8">Work Experience</h1>
                <form onSubmit={handleSubmit} className="space-y-6 md:space-y-8">
                  {experienceList.map((experience, index) => (
                    <div key={index} className="space-y-4 md:space-y-6 pb-6 md:pb-8 border-b border-primary-400">
                      {index > 0 && (
                        <div className="flex justify-end">
                          <button
                            type="button"
                            onClick={() => removeExperience(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            Remove
                          </button>
                        </div>
                      )}

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
                        <div className="col-span-1 md:col-span-3">
                          <label className="block text-sm font-medium text-primary-text mb-1">
                            Job Title <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={experience.job_title}
                            onChange={(e) => handleInputChange(index, 'job_title', e.target.value)}
                            placeholder="Add work title"
                            className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                            required
                          />
                        </div>

                        <div className="col-span-1 sm:col-span-1">
                          <label className="block text-sm font-medium text-primary-text mb-1">
                            Start Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={experience.start_date}
                            onChange={(e) => handleInputChange(index, 'start_date', e.target.value)}
                            className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                            required
                          />
                        </div>

                        <div className="col-span-1 sm:col-span-1">
                          <label className="block text-sm font-medium text-primary-500 mb-1">
                            End Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={experience.end_date}
                            onChange={(e) => handleInputChange(index, 'end_date', e.target.value)}
                            className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-primary-text mb-1">
                          Responsibilities <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          value={experience.responsibilities}
                          onChange={(e) => handleInputChange(index, 'responsibilities', e.target.value)}
                          placeholder="Add here"
                          className="w-full rounded-md border border-primary-200 px-3 md:px-4 py-2 md:py-2.5 focus:border-primary-400 focus:ring-primary-400"
                          required
                        />
                      </div>
                    </div>
                  ))}

                  <div className="flex justify-center">
                    <button
                      type="button"
                      onClick={addMoreExperience}
                      className="text-primary-500 hover:text-primary-600 font-medium"
                    >
                      + Add More Experience
                    </button>
                  </div>

                  <div className="flex flex-col sm:flex-row justify-between gap-3 sm:space-x-4 pt-4">
                    <button
                      type="button"
                      onClick={() => setIsFresher(null)}
                      className="px-4 md:px-6 py-2 md:py-2.5 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100 text-sm"
                    >
                      Back
                    </button>
                    <div className="flex flex-col sm:flex-row gap-3 sm:space-x-4">
                      <button
                        type="button"
                        onClick={() => navigate('/education-background')}
                        className="px-4 md:px-6 py-2 md:py-2.5 border border-primary-200 rounded-md text-primary-text hover:bg-primary-100 text-sm"
                      >
                        Back to Education
                      </button>
                      <button
                        type="submit"
                        className="w-full sm:w-auto px-4 md:px-6 py-2 md:py-2.5 bg-primary-500 text-primary-50 rounded-md hover:bg-opacity-90 text-sm"
                      >
                        Save & Next
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default WorkExperience;